import { useState, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Phone, MapPin, CreditCard, Send, Loader2 } from "lucide-react";
import { COMPANY, SERVICES } from "@/lib/site";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = { name: "", email: "", phone: "", company: "", service: "", message: "", sms_consent: false, website: "" };

export default function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const loadedAt = useRef(Date.now());

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  // Phone: keep only digits, max 10 (NANP). +1 is assumed/prepended on submit.
  const setPhone = (e) => {
    const digits = e.target.value.replace(/\D/g, "").slice(0, 10);
    setForm((f) => ({ ...f, phone: digits }));
  };

  const formatPhone = (digits) => {
    if (!digits) return "";
    const a = digits.slice(0, 3);
    const b = digits.slice(3, 6);
    const c = digits.slice(6, 10);
    if (digits.length <= 3) return a;
    if (digits.length <= 6) return `(${a}) ${b}`;
    return `(${a}) ${b}-${c}`;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email, and message.");
      return;
    }
    if (form.phone.length !== 10) {
      toast.error("Please enter a valid 10-digit US phone number.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, { ...form, phone: `+1${form.phone}`, elapsed_ms: Date.now() - loadedAt.current });
      toast.success("Thanks! We've received your message and will be in touch shortly.");
      setForm(EMPTY);
      loadedAt.current = Date.now();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again or call us.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="grid grid-cols-1 lg:grid-cols-2 min-h-[calc(100vh-5rem)]">
      {/* LEFT - info */}
      <div className="bg-[#2E1605] text-[#FAFAFA] px-6 md:px-12 lg:px-16 py-20 md:py-28 flex items-center">
        <div className="max-w-md">
          <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#00B4C4]">Contact</span>
          <h1 className="font-heading text-4xl md:text-6xl tracking-tight mt-4 leading-none">
            <span className="font-light">Let's get</span><br />
            <span className="font-black">you connected.</span>
          </h1>
          <p className="mt-6 text-[#D9CFC8] text-lg leading-relaxed">
            Tell us about your business and what you need. We'll follow up quickly to help.
          </p>

          <ul className="mt-12 space-y-6">
            <li className="flex items-start gap-4">
              <span className="w-11 h-11 flex items-center justify-center rounded-sm bg-white/10 text-[#00B4C4]"><Phone className="h-5 w-5" /></span>
              <div>
                <span className="block text-xs uppercase tracking-widest text-[#B8ABA2] font-bold">Phone</span>
                <a href={`tel:${COMPANY.phone}`} className="text-lg hover:text-[#00B4C4]">{COMPANY.phone}</a>
                <p data-testid="contact-sms-note" className="text-xs text-[#B8ABA2] mt-1 leading-relaxed max-w-xs">
                  Prefer texts? Opt in below to get appointment reminders & service updates by SMS. Msg &amp; data rates may apply; reply STOP to cancel.{" "}
                  <Link to="/sms-terms" data-testid="contact-sms-terms-note-link" className="text-[#00B4C4] underline">SMS Terms</Link>
                </p>
              </div>
            </li>
            <li className="flex items-start gap-4">
              <span className="w-11 h-11 flex items-center justify-center rounded-sm bg-white/10 text-[#00B4C4]"><MapPin className="h-5 w-5" /></span>
              <div><span className="block text-xs uppercase tracking-widest text-[#B8ABA2] font-bold">Area</span><span className="text-lg">{COMPANY.address}</span></div>
            </li>
          </ul>

          <a
            href={COMPANY.paymentUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="contact-payment-btn"
            className="mt-12 inline-flex items-center gap-2 bg-[#FFA500] text-[#1A110B] font-heading font-black tracking-wider uppercase text-sm px-6 py-3 rounded-sm hover:-translate-y-0.5 transition-transform"
          >
            <CreditCard className="h-4 w-4" /> Make a Payment
          </a>
        </div>
      </div>

      {/* RIGHT - form */}
      <div className="bg-white px-6 md:px-12 lg:px-16 py-20 md:py-28 flex items-center">
        <form data-testid="contact-form" onSubmit={onSubmit} className="w-full max-w-lg space-y-6">
          <h2 className="font-heading text-2xl font-black">Send us a message</h2>

          {/* Honeypot: hidden from humans, bots tend to fill it. Do not remove. */}
          <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", top: "-9999px", height: 0, width: 0, overflow: "hidden" }}>
            <label htmlFor="website">Website</label>
            <input
              id="website"
              name="website"
              type="text"
              tabIndex={-1}
              autoComplete="off"
              value={form.website}
              onChange={set("website")}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div className="space-y-2">
              <Label htmlFor="name">Name *</Label>
              <Input id="name" data-testid="contact-input-name" value={form.name} onChange={set("name")} placeholder="Jane Doe" className="focus-visible:ring-[#00B4C4]" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">Phone *</Label>
              <div className="flex items-stretch">
                <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-input bg-[#FAFAFA] text-sm font-medium text-[#594A42]" data-testid="contact-phone-prefix">+1</span>
                <Input
                  id="phone"
                  type="tel"
                  inputMode="numeric"
                  data-testid="contact-input-phone"
                  value={formatPhone(form.phone)}
                  onChange={setPhone}
                  placeholder="(225) 000-0000"
                  className="rounded-l-none focus-visible:ring-[#00B4C4]"
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div className="space-y-2">
              <Label htmlFor="email">Email *</Label>
              <Input id="email" type="email" data-testid="contact-input-email" value={form.email} onChange={set("email")} placeholder="jane@company.com" className="focus-visible:ring-[#00B4C4]" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="company">Company</Label>
              <Input id="company" data-testid="contact-input-company" value={form.company} onChange={set("company")} placeholder="Your business" className="focus-visible:ring-[#00B4C4]" />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="service">Service of interest</Label>
            <Select value={form.service} onValueChange={(v) => setForm((f) => ({ ...f, service: v }))}>
              <SelectTrigger id="service" data-testid="contact-select-service" className="focus:ring-[#00B4C4]">
                <SelectValue placeholder="Select a service" />
              </SelectTrigger>
              <SelectContent>
                {SERVICES.map((s) => (
                  <SelectItem key={s.slug} value={s.title} data-testid={`contact-service-option-${s.slug}`}>{s.title}</SelectItem>
                ))}
                <SelectItem value="General Inquiry" data-testid="contact-service-option-general">General Inquiry</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="message">Message *</Label>
            <Textarea id="message" data-testid="contact-input-message" value={form.message} onChange={set("message")} placeholder="Tell us what you need help with..." rows={5} className="focus-visible:ring-[#00B4C4]" />
          </div>

          <div className="flex items-start gap-3 rounded-sm border border-[#E5E1DE] bg-[#FAFAFA] p-4">
            <Checkbox
              id="sms-consent"
              data-testid="contact-sms-consent"
              checked={form.sms_consent}
              onCheckedChange={(v) => setForm((f) => ({ ...f, sms_consent: v === true }))}
              className="mt-0.5 data-[state=checked]:bg-[#00B4C4] data-[state=checked]:border-[#00B4C4]"
            />
            <Label htmlFor="sms-consent" className="text-xs leading-relaxed text-[#594A42] font-normal cursor-pointer">
              By checking this box, you agree to receive text messages (SMS) from Beta-Tech Systems, Inc. at the phone
              number provided, including appointment reminders, service updates, and account notifications. Consent is not
              a condition of purchase. Message frequency varies. Message and data rates may apply. Reply HELP for help or
              STOP to opt out. See our{" "}
              <Link to="/privacy" data-testid="sms-privacy-link" className="text-[#00B4C4] font-medium underline">Privacy Policy</Link>{" "}
              and{" "}
              <Link to="/terms" data-testid="sms-terms-link" className="text-[#00B4C4] font-medium underline">Terms &amp; Conditions</Link>.
            </Label>
          </div>

          <button
            type="submit"
            disabled={submitting}
            data-testid="contact-form-submit-button"
            className="w-full inline-flex items-center justify-center gap-2 bg-[#FFA500] text-[#1A110B] font-heading font-black tracking-wider uppercase text-sm px-8 py-4 rounded-sm hover:-translate-y-0.5 hover:shadow-lg transition-all disabled:opacity-60 disabled:hover:translate-y-0"
          >
            {submitting ? (<><Loader2 className="h-4 w-4 animate-spin" /> Sending...</>) : (<><Send className="h-4 w-4" /> Send Message</>)}
          </button>
          <p className="text-xs text-[#8B7A70]">Fields marked * are required. Your information is used only to respond to your inquiry and is never sold. Mobile opt-in data is never shared with third parties.</p>
        </form>
      </div>
    </div>
  );
}
